import { KnowledgeSource } from "../components/FilterSettings";

export const enum RetrievalMode {
    Hybrid = "hybrid",
    Vectors = "vectors",
    Text = "text"
}

export const enum FilterPowerplant {
    Montoir = "montoir",
    Flemalle = "flemalle",
    Chile = "chile"
}

export const enum GPT4VInput {
    TextAndImages = "textAndImages",
    Images = "images",
    Texts = "texts"
}

export const enum VectorFieldOptions {
    Embedding = "embedding",
    ImageEmbedding = "imageEmbedding",
    Both = "both"
}

export const enum FlowType {
    ChatFlow = "ChatFlow",
    SearchEventsFlow = "SearchEventsFlow"
}

export type RiskAnalysisAppRequestOverrides = {
    top?: number;
    filter_powerplant?: string;
    filters?: Record<string, string[]>;
    vector_fields: VectorFieldOptions[];
};

export type ChatAppRequestOverrides = {
    retrieval_mode?: RetrievalMode;
    semantic_ranker?: boolean;
    semantic_captions?: boolean;
    exclude_category?: string;
    top?: number;
    temperature?: number;
    minimum_search_score?: number;
    minimum_reranker_score?: number;
    prompt_template?: string;
    prompt_template_prefix?: string;
    prompt_template_suffix?: string;
    suggest_followup_questions?: boolean;
    use_oid_security_filter?: boolean;
    use_groups_security_filter?: boolean;
    use_gpt4v?: boolean;
    gpt4v_input?: GPT4VInput;
    vector_fields: VectorFieldOptions[];
    filter_powerplant?: string;
    filters?: Record<string, string[]>;
};

export type FileAttachment = {
    encoded_file: string; // base64
    file_mime_type: string;
    file_extension: string;
    filename: string;
};

export type ResponseMessage = {
    content: string;
    role: string;
    files?: FileAttachment[];
};

export type Thoughts = {
    title: string;
    description: any; // It can be any output from the api
    props?: { [key: string]: string };
};

export const enum CitationType {
    Sharepoint = "sharepoint",
    Event = "event"
}

export type SharepointCitation = {
    content_name: string;
    type: CitationType.Sharepoint;
    metadata: {
        sharepoint_uri: string;
        image_path?: string;
    };
};

export type EventCitation = {
    content_name: string;
    type: CitationType.Event;
    metadata: {
        lorinlogs_uri: string;
    };
};

export type Citation = SharepointCitation | EventCitation;

export type ResponseContext = {
    data_points: string[];
    followup_questions: string[] | null;
    thoughts: Thoughts[];
    sharepoint_uris: { [key: string]: string };
    citations?: Citation[];
    unused_files?: string[];
    powerplant: string;
};

export type ResponseChoice = {
    index: number;
    message: ResponseMessage;
    context: ResponseContext;
    session_state: any;
};

export type ChatAppResponseOrError = {
    choices?: ResponseChoice[];
    error?: string;
};

export type ChatAppResponse = {
    choices: ResponseChoice[];
};

export type ConversationSummary = {
    thread_id: string;
    title: string;
    created_at: string;
};

export type ThinkingStepEvent = {
    type: string;
    stepName?: string;
    toolCallId?: string;
    toolCallName?: string;
    delta?: string;
};

export type StoredConversationMessage = {
    id: string;
    role: "user" | "assistant";
    content: string;
    followup_questions: string[];
    thinking_steps: ThinkingStepEvent[];
    created_at: string;
    citations: Citation[];
};

export type RiskAnalysisAppRequestContext = {
    callerApp?: string;
    site?: string;
    overrides?: RiskAnalysisAppRequestOverrides;
};

export type ChatAppRequestContext = {
    callerApp?: string;
    site?: string;
    overrides?: ChatAppRequestOverrides;
    knowledge_source?: string[];
};

export type RiskAnalysisAppRequest = {
    messages: ResponseMessage[];
    context?: RiskAnalysisAppRequestContext;
    stream?: boolean;
    session_state: any;
};

export type AskElisaBotAppRequest = {
    messages: ResponseMessage[];
    context?: RiskAnalysisAppRequestContext;
    stream?: boolean;
    session_state: any;
};

export type ChatAppRequest = {
    messages: ResponseMessage[];
    context?: ChatAppRequestContext;
    stream?: boolean;
    session_state: any;
};

export type PowerplantConfig = {
    id: string;
    name: string;
    imageUrl?: string;
    knowledgeSources: KnowledgeSource[];
    uiFields: { name: string; displayName: string; values: { text: string; key: string }[] }[];
    exampleQuestions: string[];
    sharepointLibraryUrl: string;
};

export type PowerplantConfigResponseV2 = {
    id: string;
    displayName: string;
    imageUrl?: string;
    sharepointLibraryUrl?: string;
    exampleQuestions?: string[];
    knowledgeSources?: { knowledgeSource: KnowledgeSource; skills: string[] }[];
    agents?: {
        id: string;
        displayName: string;
        knowledgeSources: KnowledgeSource[];
        skills: string[];
    }[];
    skills?: { id: string; displayName: string }[];
    uiFields?: { id: string; displayName: string; values: { text: string; key: string }[] }[];
};

export interface PowerplantInfo {
    id: string;
    name?: string;
    imageUrl?: string;
}

export type Config = {
    powerplants?: Record<string, PowerplantInfo>;
};

export type SimpleAPIResponse = {
    message?: string;
};

export type ErrorDTO = {
    message: string;
    error: string;
    code: string;
    status: number;
    path?: string | null;
    details: Record<string, unknown>;
    trace_id: string;
};

export type FilterOption = {
    name: string;
    values: string[];
};

export interface GDPRContent {
    title: string;
    text: string;
    language: string;
}

export interface GDPRResponse {
    availableLanguages: Array<{
        code: string;
        name: string;
        flag: string;
    }>;
    content: Record<string, GDPRContent>;
}
